import axios from 'axios'

import JWTUtils from 'utils/jwtUtils'
import ViewerConfig from 'utils/viewerConfig'

const emptyInteractionsObject = {
  audiosPlayed: [],
  highlights: [],
  outdatedHighlights: null,
  sectionFeedbacks: [],
  sectionsCompleted: [],
  thirdPartyLinksVisited: [],
  userLinks: [],
}

const highlightsAdapterMap = (highlightsToAdapt) => {
  const highlights = []

  highlightsToAdapt?.forEach(({ sectionId, highlights: highlightsGrouped }) => {
    highlightsGrouped.forEach((highlight) => {
      highlights.push({
        characterRange: {
          end: highlight.endPosition,
          start: highlight.initialPosition,
        },
        id: highlight.highlightId,
        isValid: highlight.isValid !== null ? highlight.isValid : true,
        note: highlight.note,
        sdkId: highlights.length + 1, // Needs to be in consecutive order starting by 1
        sectionId: sectionId === 'COVER' ? '' : sectionId,
        visibleText: highlight.content,
      })
    })
  })
  return highlights
}

const splitPairs = (arr) => {
  const pairs = []
  for (let i = 0; i < arr.length; i += 2) {
    if (arr[i + 1] !== undefined) {
      pairs.push([arr[i], arr[i + 1]])
    } else {
      pairs.push([arr[i]])
    }
  }
  return pairs
}

const groupHotlinks = (pages) => {
  const newPages = pages
  pages.forEach((groupedPages, pageIndex) => {
    groupedPages.forEach((page) => {
      const media = []
      const gym = []
      const cloud = []
      if (page.hotlinks) {
        page.hotlinks.forEach((hotlink) => {
          if (hotlink.type === 'MEDIA') {
            media.push(hotlink)
          }
          if (hotlink.type === 'GYM') {
            gym.push(hotlink)
          }
          if (hotlink.type === 'CLOUD_LIBRARY') {
            cloud.push(hotlink)
          }
          const groupedHotlinks = {
            cloud,
            gym,
            media,
          }
          newPages[pageIndex].hotlinks = groupedHotlinks
        })
      }
    })
  })
  return newPages
}

const parseFlipbook = ({ flipbook, hasFlipbookCover, orientation }) => {
  const parsedFlipbook = { ...flipbook }
  if (flipbook) {
    parsedFlipbook.totalPages = flipbook.pages.length
    // Remove cover page
    if (hasFlipbookCover) {
      parsedFlipbook.pages.splice(0, 1)
    }
    // group pages in pairs
    if (orientation === 'PORTRAIT') {
      const splittedPages = splitPairs(flipbook.pages)
      parsedFlipbook.pages = groupHotlinks(splittedPages)
    }
  }
  return parsedFlipbook
}

const parseBookData = (book) => {
  const parsedBook = { ...book }
  const {
    structure,
    structure: { topics },
    flipbookCover: hasFlipbookCover,
    orientation,
  } = parsedBook
  structure.topics = topics.map(({ units, ...topic }) => {
    const parsedTopic = { ...topic }
    parsedTopic.units = units.map((unit) => {
      const parsedUnit = { ...unit }
      const { flipbook } = parsedUnit
      if (flipbook) {
        parsedUnit.flipbook = parseFlipbook({ flipbook, hasFlipbookCover, orientation })
      }
      return parsedUnit
    })
    return parsedTopic
  })
  return parsedBook
}

const BookCall = {
  async createAttempt(attempt) {
    const url = `${window.viewer.api}attempts`
    const reqConfig = {
      headers: {
        'Content-Type': 'application/jose;',
      },
    }
    const encodedJwtToken = await JWTUtils.createToken(attempt)

    try {
      const response = await axios.post(url, encodedJwtToken, reqConfig)
      return response.data
    } catch (error) {
      return error
    }
  },

  // ONLY for HTML books

  async createChatIA(bookId) {
    const url = `${window.viewer.api}chatSession`

    const response = await axios.post(url, {
      bookId,
    })

    return response.data
  },

  async createChatIAPreview(bookId, chatTypeId, language) {
    const url = `${window.viewer.api}chatSession`

    const response = await axios.post(url, {
      bookId,
      chatTypeId,
      language,
    })

    return response.data
  },

  async createHighlight(userBookId, unitId, sectionId, highlight) {
    const url = `${window.viewer.api}userBooks/${userBookId}/highlights`
    let response = await axios.post(url, {
      content: highlight.visibleText,
      endPosition: highlight.characterRange.end,
      initialPosition: highlight.characterRange.start,
      note: highlight.note,
      sectionId,
      unitId,
    })
    return response.data
  },

  // ONLY for HTML books
  async createLink(sectionId, userBookId, unitId, { description, title, url, shared }) {
    const urlQuery = `${window.viewer.api}userLinks`

    const response = await axios.post(urlQuery, {
      description,
      sectionId,
      shared,
      title,
      unitId,
      url,
      userBookId,
    })

    return response.data
  },

  async createSectionCompleted(userBookId, unitId, sectionId) {
    const urlQuery = `${window.viewer.api}userBooks/sectionCompleted`

    try {
      const response = await axios.post(urlQuery, {
        sectionId,
        unitId,
        userBookId,
      })

      return response.data
    } catch (e) {
      return e
    }
  },

  // ONLY for HTML books
  async deleteLink(userLinkId) {
    const urlQuery = `${window.viewer.api}userLinks/${userLinkId}`

    const response = await axios.delete(urlQuery)

    return response.data
  },

  async getAttempts(questionIds, timestampModified, userBookIds) {
    const params = { questionIds, timestampModified, userBookIds }
    const url = `${window.viewer.api}attempts`
    try {
      const response = await axios.get(url, { params })
      return response.data
    } catch (error) {
      return error
    }
  },

  async getBookStatics() {
    const url = `${window.viewer.api}books/statics`
    const statics = sessionStorage.getItem('bookStatics')
    if (!statics) {
      try {
        const response = await axios.get(url)
        sessionStorage.setItem('bookStatics', JSON.stringify(response.data))
        return response.data
      } catch (error) {
        return error
      }
    }
    return JSON.parse(statics)
  },

  // WARNING: this method is only for NON html books
  async getBookmark(userBookId) {
    if (!userBookId) {
      return null
    }
    const url = `${window.viewer.api}userBooks/${userBookId}/bookmarks`
    try {
      const response = await axios.get(url)
      return response.data
    } catch (error) {
      return null
    }
  },

  async getBookmarkAndDraft(userBookId) {
    if (!userBookId) {
      return null
    }
    const url = `${window.viewer.api}userBooks/${userBookId}/bookmarkAndDraft`
    try {
      const response = await axios.get(url)
      return response.data
    } catch (error) {
      return null
    }
  },

  async getHTML(unitId) {
    if (!unitId) return null

    try {
      const response = await axios.get(
        `http://q4-book-generator.vicensvivesdigital.pre/unit/${unitId}?refresh=true`
      )
      return response.data
    } catch (error) {
      return error
    }
  },

  async getHtmlBookmark(userBookId) {
    if (!userBookId) {
      return null
    }
    const url = `${window.viewer.api}userBooks/${userBookId}/htmlBookmarks`
    try {
      const response = await axios.get(url)
      return response.data
    } catch (error) {
      return null
    }
  },

  async getResults(userBookId, unitId) {
    const params = { unitId }
    const url = `${window.viewer.api}userBooks/${userBookId}/results`
    try {
      let counter = 0
      let response = await axios.get(url, { params })
      if (!response && counter === 0) {
        response = await axios.get(url, { params })
        counter = 1
      }
      return response.data
    } catch (error) {
      return error
    }
  },

  async getUnit(unitId, legacy, book = {}) {
    const params = { legacy }
    const url = `${window.viewer.api}units/${unitId}`
    const { flipbookCover: hasFlipbookCover, orientation } = book
    try {
      let counter = 0
      let response = await axios.get(url, { params })
      if ((!response || !response.data) && counter === 0) {
        response = await axios.get(url, { params })
        counter = 1
      }
      const { data } = response

      if (data.flipbook) {
        data.flipbook = parseFlipbook({
          flipbook: data.flipbook,
          hasFlipbookCover,
          orientation,
        })
      }
      return data
    } catch (error) {
      return error
    }
  },

  async getUnitAutogenerated(unitId) {
    const params = { legacy: false }
    const url = `${window.viewer.api}units/${unitId}`
    try {
      let response = await axios.get(url, { params })
      return response.data
    } catch (error) {
      return error
    }
  },

  async getUserBook(bookId, unitId, sectionId) {
    const userBookId = sessionStorage.getItem('userBookId')
    const params = {
      bookId,
      referral: window?.location?.host?.includes('academ') ? 'academy' : 'edubook',
      sectionId,
      unitId,
      userBookId: userBookId || null,
    }
    const url = `${window.viewer.api}book`
    let response = await axios.get(url, { params })
    if (!response || !response.data || !response.data.book) {
      response = await axios.get(url, { params })
    }
    if (!response || !response.data || !response.data.book) {
      throw {
        code: response?.data?.code || 'BOOK_DATA_EMPTY',
        data: response?.data || null,
        message: 'Book data is empty or unavailable',
        status: response?.status || 500,
      }
    }

    const { data } = response

    if (!response.data.book?.subtitle) {
      data.book.subtitle = ''
    }
    if (!data.legacy) {
      data.book = parseBookData(data.book)
      if (data.unit) {
        const {
          book: { flipbookCover: hasFlipbookCover, orientation },
          unit: { flipbook },
        } = data
        data.unit.flipbook = parseFlipbook({ flipbook, hasFlipbookCover, orientation })
      }
    }

    return data
  },

  async getUserBooksIndex(bookId, legacy) {
    const params = { legacy }
    const url = `${window.viewer.api}books/${bookId}/index`
    try {
      let counter = 0
      let response = await axios.get(url, { params })
      if ((!response || !response.data) && counter === 0) {
        response = await axios.get(url, { params })
        counter = 1
      }
      const book = !legacy ? parseBookData(response.data) : response.data
      return book
    } catch (error) {
      window.location = window.viewer.edubookUrl
      return error
    }
  },

  async getUserStatics() {
    const url = `${window.viewer.api}users/statics`
    const statics = sessionStorage.getItem('userStatics')
    if (!statics) {
      try {
        const response = await axios.get(url)
        sessionStorage.setItem('userStatics', JSON.stringify(response.data))
        return response.data
      } catch (error) {
        return error
      }
    }
    return JSON.parse(statics)
  },

  async getUserUnitInteractions(userBookId, unitId, classId, updatedAt) {
    if (!userBookId || !unitId || !updatedAt) return null

    if (ViewerConfig.isPreviewOrOpen(userBookId)) {
      return emptyInteractionsObject
    }
    const urlParam = classId
      ? `?classId=${classId}&htmlUpdatedAt=${updatedAt}`
      : `?htmlUpdatedAt=${updatedAt}`
    const url = `${window.viewer.api}interactions/${userBookId}/units/${unitId}${urlParam}`

    try {
      const response = await axios.get(url)
      const {
        audiosPlayed,
        sectionHighlights,
        outdatedHighlights,
        sectionFeedbacks,
        sectionsCompleted,
        thirdPartyLinksVisited,
        userLinks,
      } = response.data
      const highlights = highlightsAdapterMap(sectionHighlights)
      return {
        audiosPlayed: audiosPlayed || [],
        highlights: highlights || [],
        outdatedHighlights: outdatedHighlights || false,
        sectionFeedbacks: sectionFeedbacks || [],
        sectionsCompleted: sectionsCompleted.map((s) => s.sectionId),
        thirdPartyLinksVisited: thirdPartyLinksVisited.map((t) => t.url) || [],
        userLinks: userLinks || [],
      }
    } catch (error) {
      return emptyInteractionsObject
    }
  },

  async removeHighlight(highlightId) {
    const url = `${window.viewer.api}userBooks/highlights/${highlightId}`
    let response = await axios.delete(url)
    return response.data
  },

  async removeHighlights(highlightIds) {
    const url = `${window.viewer.api}userBooks/highlights`
    let response = axios.delete(url, { data: highlightIds })
    return response.data
  },

  async removeSectionFeedback(userBookId, sectionId) {
    try {
      const url = `${window.viewer.api}sectionFeedback/${userBookId}/sections/${sectionId}`
      const response = await axios.delete(url)
      return response.data
    } catch (error) {
      // The feedback does not exist in database
      if (error.response?.status === 404) {
        return null
      }
      throw error
    }
  },

  async updateAudioPlayed(userBookId, unitId, audioId) {
    if (!userBookId || !unitId || !audioId) return null

    const url = `${window.viewer.api}userBooks/audioPlayed`

    try {
      const response = await axios.post(url, { audioId, unitId, userBookId })

      return response
    } catch (error) {
      return error
    }
  },

  async updateBookmark(userBookId, bookmark) {
    if (!userBookId || !bookmark) {
      return null
    }
    const url = `${window.viewer.api}userBooks/${userBookId}/bookmarks`
    try {
      const response = await axios.put(url, bookmark)
      return response.data
    } catch (error) {
      return error
    }
  },

  // ONLY for HTML books
  async updateHighlight(highlightId, note) {
    const url = `${window.viewer.api}userBooks/highlights/${highlightId}`
    let response = await axios.patch(url, {
      note: note,
    })
    return response.data
  },

  async updateHighlightsLocation(highlights) {
    const url = `${window.viewer.api}userBooks/highlights/`
    let response = await axios.patch(url, highlights)
    return response.data
  },

  async updateHtmlBookmark(userBookId, bookmark) {
    if (!userBookId || !bookmark) {
      return null
    }
    const url = `${window.viewer.api}userBooks/${userBookId}/htmlBookmarks`
    try {
      const response = await axios.put(url, bookmark)
      return response.data
    } catch (error) {
      return error
    }
  },

  // ONLY for HTML books
  async updateLink(sectionId, userBookId, unitId, { description, title, userLinkId, url, shared }) {
    const urlQuery = `${window.viewer.api}userLinks/${userLinkId}`

    const response = await axios.patch(urlQuery, {
      description,
      sectionId,
      shared,
      title,
      unitId,
      url,
      userBookId,
      userLinkId,
    })

    return response.data
  },

  async updateSectionFeedback(userBookId, unitId, sectionId, value, comment, checkBoxIds) {
    const url = `${window.viewer.api}sectionFeedback/${userBookId}/sections/${sectionId}`
    const response = await axios.put(url, {
      checkBoxIds,
      comment,
      liked: value === 1,
      unitId,
    })

    return response.data
  },

  async updateThirdPartyVisitedLink(userBookId, unitId, url) {
    if (!ViewerConfig.isPreview() && !ViewerConfig.isOpen()) {
      try {
        const response = await axios.post(`${window.viewer.api}userBooks/thirdPartyLinkVisited`, {
          unitId,
          url,
          userBookId,
        })

        return response
      } catch (error) {
        return error
      }
    }

    return []
  },
}

export default BookCall
